import React, { forwardRef, useEffect, useState } from "react"
import { FaCloudUploadAlt, FaFile, FaTimesCircle } from "react-icons/fa"
import { v4 } from 'uuid'
import { uploadFile } from "../services/fileUploadService"
import { toast } from "react-toastify"
import { AgentDocResult, errorToString, SolarDocStatus, UploadedDoc } from "@oneethos/shared"
import './image-uploader.scss'
import { Spinner, SpinnerSize } from "@fluentui/react"

type ImageUploaderProps = {
  onChange?: (d: UploadedDoc, status?: SolarDocStatus) => void
  onRemove?: () => void
  onClick?: () => void
  src?: string
  label?: string
  prompt?: string
  acceptContentTypes?: string
  doc?: UploadedDoc
  logoPreviewStyles?: React.CSSProperties
  status?: SolarDocStatus | AgentDocResult
}

export const ImageUploader = forwardRef(({
  label,
  prompt,
  onChange,
  onRemove,
  onClick,   // enables overriding the default label onclick handler
  src,
  acceptContentTypes = 'image/*',
  doc,
  logoPreviewStyles = {},
  status
}: ImageUploaderProps, ref: React.RefObject<HTMLInputElement>) => {
  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const [preview, setPreview] = useState('')
  const [uploading, setUploading] = useState(false)
  const [inputId,] = useState(v4())
  const id = `file_input_${inputId}`
  // const fileInputRef = useRef<HTMLInputElement>(null)

  // create a preview as a side effect, whenever selected file is changed
  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined)
      return
    }

    let objectUrl = undefined
    if (selectedFile.type.startsWith('image/')) {
      objectUrl = URL.createObjectURL(selectedFile)
      setPreview(objectUrl)
    } else {
      setPreview(undefined)
    }

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl)
  }, [selectedFile])

  const bgUrl = preview || src

  return <div className="image-uploader">
    {label ? <div className="mb-2 fw-bold">{label}</div> : null}
    <label className="upload-prompt" {...(onClick ? { onClick } : { htmlFor: id })}>
      <div className="logo-preview" style={{ backgroundImage: `url(${bgUrl})`, ...logoPreviewStyles }}>
        {!bgUrl ? <div className="doc pt-2">
          {uploading ? <Spinner size={SpinnerSize.large} /> : (
            doc?.label ? <>
              <FaFile size={32} />
              <div className="doc-label p-1 text-truncate">{doc.label}</div>
            </> : <>
              <FaCloudUploadAlt color="gray" size={50} />
              <div>{prompt || ''}</div>
            </>
          )}
        </div> : null}
        <div className="logo-prompt">
          <div className="lp-up">{doc ? 'Click to Edit' : 'Upload File'}</div>
        </div>
        {onRemove && (preview || src || doc) ? <div
          className="clear-img" onClick={ev => {
            ev.preventDefault()
            setPreview(undefined)
            if (ref) {
              ref.current.value = ''
            }
            onRemove()
          }}>
          <FaTimesCircle />
        </div> : null}
      </div>
    </label>
    <input
      ref={ref}
      type="file"
      id={id}
      accept={acceptContentTypes}
      disabled={uploading}
      className="form-control-file"
      style={{ display: 'none' }}
      onChange={ev => {
        if (!ev.target.files || ev.target.files.length === 0) {
          setSelectedFile(undefined)
          return
        } else {
          onChange(ev.target.files[0] as unknown as UploadedDoc, SolarDocStatus.PendingUpload)
        }

        setUploading(true)
        setSelectedFile(ev.target.files[0])

        uploadFile(ev.target.files?.[0] as File).then(res => {
          onChange(res, SolarDocStatus.Uploaded)
        }).catch(ex => toast.error(errorToString(ex))).finally(() => setUploading(false))
      }}
    />
  </div>
})

export default ImageUploader